import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Camera, MapPin, ImageIcon, Film, ChevronDown, X } from "lucide-react";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function mediaSrc(b) {
  return b.bild_url?.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL}${b.bild_url}`
    : b.bild_url;
}

// Defensiv detektion: media_typ='video' ELLER bild_url-ändelse är en videofiltyp.
// Detta gör att äldre poster utan media_typ men med .mp4-URL ändå renderas som video.
const VIDEO_EXTS_RE = /\.(mp4|mov|webm|m4v|m4a|ogg|ogv)(\?|$)/i;
function isVideoMedia(b) {
  if ((b?.media_typ || "").toLowerCase() === "video") return true;
  return VIDEO_EXTS_RE.test(b?.bild_url || "");
}

export default function BilderFranBygden() {
  const { t } = useTranslation();
  const [bilder, setBilder] = useState([]);
  const [teman, setTeman] = useState([]);
  const [activeTema, setActiveTema] = useState(null);
  const [activeFotograf, setActiveFotograf] = useState(null);
  const [fotografOpen, setFotografOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [loading, setLoading] = useState(true);

  usePageMeta({
    title: "Ditt foto – vidalv.live",
    description: "Bilder från bygden vid Fjällsjöälven – årstider, vyer, natur och vardag i Backe och Rossön. Skickade av läsare och redaktion.",
    path: "/bilder",
  });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/bilder`),
      axios.get(`${API}/bilder/teman`),
    ]).then(([bilderRes, temaRes]) => {
      setBilder(bilderRes.data);
      setTeman(temaRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Fotografenlista (sortiert, mit Anzahl)
  const fotografer = useMemo(() => {
    const map = new Map();
    bilder.forEach(b => {
      const name = (b.fotograf || "Okänd").trim();
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .map(([namn, antal]) => ({ namn, antal }))
      .sort((a, b) => a.namn.localeCompare(b.namn, "sv"));
  }, [bilder]);

  const filteredBilder = useMemo(() => {
    let list = [...bilder];
    if (activeTema) list = list.filter(b => b.tema === activeTema);
    if (activeFotograf) list = list.filter(b => (b.fotograf || "Okänd").trim() === activeFotograf);
    return list;
  }, [bilder, activeTema, activeFotograf]);

  const aktivaTeman = teman.filter(t => t.aktiv);

  // Ett karussell per fotograf (när ingen specifik filter aktiv)
  const groupedByFotograf = useMemo(() => {
    const m = new Map();
    bilder.forEach(b => {
      const name = (b.fotograf || "Okänd").trim();
      if (!m.has(name)) m.set(name, []);
      m.get(name).push(b);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], "sv"));
  }, [bilder]);

  return (
    <div data-testid="bilder-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">{t("nav.utstallning")}</p>
          <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">Bygden genom era ögon</h1>
          <p className="text-sm text-bark max-w-xl leading-relaxed">
            En öppen bildsamling från Backe, Rossön och bygderna &ndash; foton och videor inskickade av dem som bor här. Alla bidrag visas.
          </p>
        </div>
        <Link to="/bilder/skicka" data-testid="link-skicka-bild"
          className="inline-flex items-center gap-2 bg-falu hover:bg-falu-hover text-parchment font-body font-semibold text-sm px-4 py-2.5 rounded-sm transition-colors no-underline shrink-0">
          <Camera size={18} /> Skicka in bild
        </Link>
      </div>

      {/* Filter: Fotografen-Dropdown + Teman */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {fotografer.length > 0 && (
          <div data-testid="fotograf-dropdown" className="relative">
            <button
              data-testid="fotograf-toggle"
              onClick={() => setFotografOpen(!fotografOpen)}
              className={`inline-flex items-center gap-2 text-sm font-body font-semibold px-4 py-2 rounded-sm border transition-all ${
                activeFotograf
                  ? "bg-pine text-parchment border-pine"
                  : "bg-parchment text-ink border-warm-border hover:border-bark/40"
              }`}
            >
              <Camera size={14} />
              {activeFotograf ? `Fotograf: ${activeFotograf}` : "Välj fotograf"}
              <ChevronDown size={14} className={fotografOpen ? "rotate-180 transition-transform" : "transition-transform"} />
            </button>
            {fotografOpen && (
              <div className="absolute top-full left-0 mt-1 bg-parchment border border-warm-border rounded-sm shadow-lg min-w-[220px] max-h-[320px] overflow-y-auto z-30">
                <button
                  onClick={() => { setActiveFotograf(null); setFotografOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-bone-alt transition-colors ${activeFotograf === null ? "bg-bone-alt font-semibold" : ""}`}
                >
                  Alla fotografer ({bilder.length})
                </button>
                <div className="border-t border-warm-border/50" />
                {fotografer.map(f => (
                  <button
                    key={f.namn}
                    data-testid={`fotograf-option-${f.namn}`}
                    onClick={() => { setActiveFotograf(f.namn); setFotografOpen(false); }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-bone-alt transition-colors flex justify-between items-center gap-3 ${activeFotograf === f.namn ? "bg-bone-alt font-semibold" : ""}`}
                  >
                    <span>{f.namn}</span>
                    <span className="text-xs text-bark/50">{f.antal}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {activeFotograf && (
          <button onClick={() => setActiveFotograf(null)} data-testid="clear-fotograf"
            className="text-sm text-falu hover:text-falu-hover inline-flex items-center gap-1">
            <X size={12} /> Rensa
          </button>
        )}
      </div>

      {/* Teman */}
      {aktivaTeman.length > 0 && !activeFotograf && (
        <section data-testid="teman-section" className="mb-10">
          <h2 className="font-heading text-xl font-medium text-ink mb-3">Teman</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {aktivaTeman.map((tema) => (
              <button key={tema.slug} data-testid={`tema-${tema.slug}`}
                onClick={() => setActiveTema(activeTema === tema.slug ? null : tema.slug)}
                className={`text-left p-4 border border-warm-border rounded-sm transition-all ${activeTema === tema.slug ? "bg-pine text-parchment border-pine" : "bg-parchment hover:border-bark/40"}`}>
                <div className="flex items-center gap-2 mb-1">
                  <ImageIcon size={16} className={activeTema === tema.slug ? "text-parchment/80" : "text-pine"} />
                  <h3 className={`font-heading text-base font-semibold ${activeTema === tema.slug ? "text-parchment" : "text-ink"}`}>{tema.namn}</h3>
                </div>
                <p className={`text-sm ${activeTema === tema.slug ? "text-parchment/80" : "text-bark"}`}>{tema.beskrivning}</p>
              </button>
            ))}
          </div>
          {activeTema && (
            <button onClick={() => setActiveTema(null)} data-testid="clear-tema-filter" className="mt-3 text-sm text-falu hover:text-falu-hover">
              Visa alla bilder
            </button>
          )}
        </section>
      )}

      {loading && <p className="text-bark/60 py-8">Laddar bildsamlingen...</p>}

      {/* När ingen filter är aktiv: gruppera per fotograf med karussell */}
      {!loading && !activeTema && !activeFotograf && groupedByFotograf.length > 0 && (
        <div data-testid="fotograf-groups" className="flex flex-col gap-12">
          {groupedByFotograf.map(([namn, items]) => (
            <FotografGroup key={namn} namn={namn} items={items} onOpen={setLightbox} />
          ))}
        </div>
      )}

      {/* När filter aktiv: platt grid */}
      {!loading && (activeTema || activeFotograf) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredBilder.length === 0 ? (
            <p className="sm:col-span-3 text-bark/60 py-8">Inga bilder matchar filtret.</p>
          ) : filteredBilder.map(b => <BildCard key={b.id} bild={b} onOpen={() => setLightbox(b)} />)}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox bild={lightbox} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

function FotografGroup({ namn, items, onOpen }) {
  return (
    <section data-testid={`fotograf-group-${namn}`} className="">
      <div className="flex items-end justify-between mb-3 border-b border-warm-border pb-2">
        <div>
          <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em]">Fotograf</p>
          <h2 className="font-heading text-2xl font-semibold text-ink">{namn}</h2>
        </div>
        <span className="text-xs text-bark/60">{items.length} bidrag</span>
      </div>
      {/* Horizontellt rullande karussell */}
      <div className="overflow-x-auto pb-3 -mx-4 sm:-mx-6 px-4 sm:px-6 scrollbar-thin">
        <div className="flex gap-3 min-w-min">
          {items.map(b => (
            <div key={b.id} className="w-[280px] sm:w-[320px] shrink-0">
              <BildCard bild={b} onOpen={() => onOpen(b)} compact />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function BildCard({ bild, onOpen, compact }) {
  const isVideo = isVideoMedia(bild);
  return (
    <button
      data-testid={`bild-${bild.id}`}
      onClick={onOpen}
      className="text-left bg-parchment border border-warm-border rounded-sm overflow-hidden group hover:border-bark/40 transition-colors w-full"
    >
      <div className={`${compact ? "aspect-[4/3]" : "aspect-[4/3]"} overflow-hidden relative bg-ink flex items-center justify-center`}>
        {isVideo ? (
          <>
            <video
              src={`${mediaSrc(bild)}#t=0.5`}
              muted
              playsInline
              preload="metadata"
              className="w-full h-full object-cover"
            />
            <span className="absolute top-2 left-2 flex items-center gap-1 bg-ink/75 text-parchment text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-sm">
              <Film size={10} /> Video{bild.varaktighet_sek ? ` · ${Math.round(bild.varaktighet_sek)}s` : ""}
            </span>
            <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="w-14 h-14 rounded-full bg-parchment/85 flex items-center justify-center shadow-lg">
                <span className="w-0 h-0 border-l-[14px] border-l-ink border-t-[9px] border-t-transparent border-b-[9px] border-b-transparent ml-1" />
              </span>
            </span>
          </>
        ) : (
          <img
            src={mediaSrc(bild)}
            alt={bild.titel}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        )}
      </div>
      <div className="p-4">
        <h3 className="font-heading text-base font-medium text-ink mb-1 line-clamp-1">{bild.titel}</h3>
        {bild.beskrivning && <p className="text-sm text-bark/70 line-clamp-2 mb-2">{bild.beskrivning}</p>}
        <div className="flex items-center justify-between text-xs text-bark/60">
          <span>Av: {bild.fotograf || "Okänd"}</span>
          {bild.ort && <span className="flex items-center gap-1"><MapPin size={12} /> {bild.ort}</span>}
        </div>
        {bild.tema && (
          <span className="inline-block mt-2 text-xs font-medium bg-pine/10 text-pine px-2 py-0.5 rounded">{bild.tema}</span>
        )}
      </div>
    </button>
  );
}

function Lightbox({ bild, onClose }) {
  const isVideo = isVideoMedia(bild);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);
  return (
    <div data-testid="lightbox" className="fixed inset-0 z-[100] bg-ink/95 flex items-center justify-center p-4 sm:p-10"
      onClick={onClose}>
      <button
        data-testid="lightbox-close"
        onClick={onClose}
        className="absolute top-4 right-4 text-parchment/80 hover:text-parchment p-2"
        aria-label="Stäng"
      >
        <X size={28} />
      </button>
      <div className="max-w-5xl max-h-full w-full flex flex-col items-center gap-4" onClick={(e) => e.stopPropagation()}>
        <div className="w-full flex items-center justify-center" style={{ maxHeight: "75vh" }}>
          {isVideo ? (
            <video
              data-testid="lightbox-video"
              src={mediaSrc(bild)}
              controls
              autoPlay
              playsInline
              className="max-w-full max-h-[75vh] object-contain"
            />
          ) : (
            <img
              data-testid="lightbox-image"
              src={mediaSrc(bild)}
              alt={bild.titel}
              className="max-w-full max-h-[75vh] object-contain"
            />
          )}
        </div>
        <div className="text-center text-parchment max-w-2xl">
          <h2 className="font-heading text-xl sm:text-2xl font-semibold mb-1">{bild.titel}</h2>
          {bild.beskrivning && <p className="text-sm text-parchment/80 mb-2 leading-relaxed">{bild.beskrivning}</p>}
          <p className="text-xs text-parchment/50">
            Av: {bild.fotograf || "Okänd"}
            {bild.ort && ` · ${bild.ort}`}
            {bild.tema && ` · Tema: ${bild.tema}`}
          </p>
        </div>
      </div>
    </div>
  );
}
